import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface PlannerActivityRow {
  id: string;
  event_type: string;
  entity_type: string;
  entity_id: string | null;
  service_plan_id: string | null;
  actor_user_id: string | null;
  summary: string;
  details: Record<string, unknown>;
  created_at: string;
  actor_user?: {
    id: string;
    full_name?: string | null;
    email?: string | null;
  } | Array<{
    id: string;
    full_name?: string | null;
    email?: string | null;
  }> | null;
}

export interface PlannerActivityItem {
  id: string;
  summary: string;
  actorName: string;
  createdAt: string;
  servicePlanId: string | null;
  eventType: string;
  entityType: string;
  activityLabel: string;
  activityIcon: 'availability' | 'role' | 'service' | 'assignment' | 'generic';
  activityTone: 'info' | 'success' | 'warning';
}

function mapActivityMeta(row: PlannerActivityRow): {
  label: string;
  icon: 'availability' | 'role' | 'service' | 'assignment' | 'generic';
  tone: 'info' | 'success' | 'warning';
} {
  const entity = (row.entity_type || '').toLowerCase();
  const event = (row.event_type || '').toLowerCase();
  const summary = (row.summary || '').toLowerCase();

  if (entity === 'service_plan_availability') {
    if (summary.includes('marked not available')) {
      return { label: 'Marked Not Available', icon: 'availability', tone: 'warning' };
    }
    if (summary.includes('marked available')) {
      return { label: 'Marked Available', icon: 'availability', tone: 'success' };
    }
    if (event === 'delete') {
      return { label: 'Availability Removed', icon: 'availability', tone: 'warning' };
    }
    if (event === 'insert') {
      return { label: 'Availability Added', icon: 'availability', tone: 'success' };
    }
    return { label: 'Availability Updated', icon: 'availability', tone: 'info' };
  }

  if (entity === 'service_member_unavailability') {
    if (event === 'delete') {
      return { label: 'Calendar Unavailability Removed', icon: 'availability', tone: 'warning' };
    }
    if (event === 'insert') {
      return { label: 'Calendar Unavailability Added', icon: 'availability', tone: 'success' };
    }
    return { label: 'Calendar Unavailability Updated', icon: 'availability', tone: 'info' };
  }

  if (entity === 'users' && /default planner role/i.test(row.summary || '')) {
    return { label: 'Default Role Changed', icon: 'role', tone: 'info' };
  }

  if (entity === 'service_plan_assignments' || entity === 'service_plan_subsection_assignments') {
    return { label: 'Assignment Change', icon: 'assignment', tone: 'info' };
  }

  if (entity === 'service_plans') {
    return { label: 'Service Plan Update', icon: 'service', tone: 'info' };
  }

  return { label: 'Planner Activity', icon: 'generic', tone: 'info' };
}

function pickActorName(row: PlannerActivityRow): string {
  const actor = Array.isArray(row.actor_user) ? row.actor_user[0] : row.actor_user;
  if (actor?.full_name?.trim()) return actor.full_name.trim();
  if (actor?.email?.trim()) return actor.email.trim();
  return 'System';
}

export function formatRelativeTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return 'Just now';

  const seconds = Math.round((parsed.getTime() - Date.now()) / 1000);
  const absSeconds = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  if (absSeconds < 60) return rtf.format(seconds, 'second');

  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');

  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');

  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return rtf.format(days, 'day');

  const months = Math.round(days / 30);
  if (Math.abs(months) < 12) return rtf.format(months, 'month');

  const years = Math.round(months / 12);
  return rtf.format(years, 'year');
}

export function usePlannerActivity(enabled: boolean, limit = 25) {
  const activityQuery = useQuery({
    queryKey: ['church-planner', 'activity', limit],
    queryFn: async (): Promise<PlannerActivityRow[]> => {
      const { data, error } = await supabase
        .from('planner_activity_log')
        .select(
          `id, event_type, entity_type, entity_id, service_plan_id, actor_user_id, summary, details, created_at, actor_user:users(id, full_name, email)`
        )
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data ?? []) as PlannerActivityRow[];
    },
    enabled,
  });

  const items = useMemo<PlannerActivityItem[]>(() => {
    return (activityQuery.data ?? []).map((row) => ({
      ...(() => {
        const meta = mapActivityMeta(row);
        return {
          eventType: row.event_type,
          entityType: row.entity_type,
          activityLabel: meta.label,
          activityIcon: meta.icon,
          activityTone: meta.tone,
        };
      })(),
      id: row.id,
      summary: row.summary,
      actorName: pickActorName(row),
      createdAt: row.created_at,
      servicePlanId: row.service_plan_id,
    }));
  }, [activityQuery.data]);

  return {
    ...activityQuery,
    items,
  };
}
